import React, { useState, useEffect } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { useTenant } from '@/contexts/TenantContext';
import { ThemeConfig, PRESET_THEMES, SEOConfig } from '@/types/theme';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { Palette, Image, Upload, Check, Loader2, Plus, Edit, Trash2, LayoutDashboard, Search } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import { EnhancedThemeEditor } from './EnhancedThemeEditor';
import { getColorContrast } from '@/utils/themeUtils';

// Helper function to get adaptive sidebar color
const getAdaptiveSidebarColor = (primary: string, background: string): string => {
  const isLight = getColorContrast(background) === 'light';
  
  if (isLight) {
    // For light backgrounds, create a dark sidebar that complements the primary color
    const primaryIsDark = getColorContrast(primary) === 'dark';
    
    if (primaryIsDark) {
      // If primary is dark, use it as the sidebar color
      return primary;
    } else {
      // If primary is light, use a dark neutral color
      return '#1f2937'; // Dark gray that works well with most themes
    }
  } else {
    // For dark backgrounds, use a lighter sidebar
    const primaryIsLight = getColorContrast(primary) === 'light';
    
    if (primaryIsLight) {
      // If primary is light, use it as the sidebar color
      return primary;
    } else {
      // If primary is dark, use a light neutral color
      return '#f3f4f6'; // Light gray that works well with most themes
    }
  }
};

export const ThemeTab: React.FC = () => {
  const { 
    currentTheme, 
    themeSettings, 
    setTheme, 
    updateCustomLogo, 
    updateCustomFavicon, 
    updateCustomSEO,
    createCustomTheme,
    updateCustomTheme,
    deleteCustomTheme,
    updateSidebarColor,
    isLoading, 
    canManageTheme 
  } = useTheme();
  const { getCurrentTenantId } = useTenant();
  

  
  const { toast } = useToast();
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [logoUrl, setLogoUrl] = useState(themeSettings.customLogo || currentTheme.logo || '');
  const [faviconUrl, setFaviconUrl] = useState(themeSettings.customFavicon || currentTheme.favicon || '');
  const [previewLogoUrl, setPreviewLogoUrl] = useState(themeSettings.customLogo || currentTheme.logo || '');
  const [previewFaviconUrl, setPreviewFaviconUrl] = useState(themeSettings.customFavicon || currentTheme.favicon || '');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [faviconFile, setFaviconFile] = useState<File | null>(null);
  const [editingTheme, setEditingTheme] = useState<ThemeConfig | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  
  // SEO state variables
  const [seoSettings, setSeoSettings] = useState<SEOConfig>({
    pageTitle: themeSettings.customSEO?.pageTitle || currentTheme.seo?.pageTitle || 'Joules Project Management',
    metaDescription: themeSettings.customSEO?.metaDescription || currentTheme.seo?.metaDescription || '',
    metaKeywords: themeSettings.customSEO?.metaKeywords || currentTheme.seo?.metaKeywords || '',
    ogTitle: themeSettings.customSEO?.ogTitle || currentTheme.seo?.ogTitle || '',
    ogDescription: themeSettings.customSEO?.ogDescription || currentTheme.seo?.ogDescription || '',
    ogImage: themeSettings.customSEO?.ogImage || currentTheme.seo?.ogImage || '',
    twitterCard: themeSettings.customSEO?.twitterCard || currentTheme.seo?.twitterCard || 'summary_large_image',
    twitterTitle: themeSettings.customSEO?.twitterTitle || currentTheme.seo?.twitterTitle || '',
    twitterDescription: themeSettings.customSEO?.twitterDescription || currentTheme.seo?.twitterDescription || '',
    twitterImage: themeSettings.customSEO?.twitterImage || currentTheme.seo?.twitterImage || '',
    canonicalUrl: themeSettings.customSEO?.canonicalUrl || currentTheme.seo?.canonicalUrl || '',
    robotsMeta: themeSettings.customSEO?.robotsMeta || currentTheme.seo?.robotsMeta || 'index, follow',
  });
  const [seoHasChanges, setSeoHasChanges] = useState(false);
  const [savingSEO, setSavingSEO] = useState(false);

  // Update SEO settings when theme or themeSettings change
  useEffect(() => {
    const newSeoSettings: SEOConfig = {
      pageTitle: themeSettings.customSEO?.pageTitle || currentTheme.seo?.pageTitle || 'Joules Project Management',
      metaDescription: themeSettings.customSEO?.metaDescription || currentTheme.seo?.metaDescription || '',
      metaKeywords: themeSettings.customSEO?.metaKeywords || currentTheme.seo?.metaKeywords || '',
      ogTitle: themeSettings.customSEO?.ogTitle || currentTheme.seo?.ogTitle || '',
      ogDescription: themeSettings.customSEO?.ogDescription || currentTheme.seo?.ogDescription || '',
      ogImage: themeSettings.customSEO?.ogImage || currentTheme.seo?.ogImage || '',
      twitterCard: themeSettings.customSEO?.twitterCard || currentTheme.seo?.twitterCard || 'summary_large_image',
      twitterTitle: themeSettings.customSEO?.twitterTitle || currentTheme.seo?.twitterTitle || '',
      twitterDescription: themeSettings.customSEO?.twitterDescription || currentTheme.seo?.twitterDescription || '',
      twitterImage: themeSettings.customSEO?.twitterImage || currentTheme.seo?.twitterImage || '',
      canonicalUrl: themeSettings.customSEO?.canonicalUrl || currentTheme.seo?.canonicalUrl || '',
      robotsMeta: themeSettings.customSEO?.robotsMeta || currentTheme.seo?.robotsMeta || 'index, follow',
    };
    setSeoSettings(newSeoSettings);
    setSeoHasChanges(false);
  }, [currentTheme, themeSettings.customSEO]);

  // Cleanup preview URLs when component unmounts
  useEffect(() => {
    return () => {
      if (previewLogoUrl && previewLogoUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewLogoUrl);
      }
      if (previewFaviconUrl && previewFaviconUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewFaviconUrl);
      }
    };
  }, [previewLogoUrl, previewFaviconUrl]);

  const handleThemeChange = async (theme: ThemeConfig) => {
    if (!canManageTheme) {
      toast({
        title: 'Access Denied',
        description: 'Only administrators can change theme settings',
        variant: 'destructive',
      });
      return;
    }

    try {
      await setTheme(theme.id);
      toast({
        title: 'Theme updated',
        description: `Switched to ${theme.name} theme`,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update theme',
        variant: 'destructive',
      });
    }
  };

  const handleLogoFileSelect = (file: File) => {
    if (!file) return;
    
    setLogoFile(file);
    // Create a preview URL for the selected file
    const previewUrl = URL.createObjectURL(file);
    setPreviewLogoUrl(previewUrl);
    
    toast({
      title: 'Logo selected',
      description: 'Click "Update Logo" to save the changes',
    });
  };

  const handleLogoUpload = async () => {
    if (!logoFile) {
      toast({
        title: 'No file selected',
        description: 'Please select a logo file first',
        variant: 'destructive',
      });
      return;
    }

    setUploadingLogo(true);
    try {
      
      // Get the current tenant ID dynamically
      const tenantId = await getCurrentTenantId();
      
      const fileExt = logoFile.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `${tenantId}/${fileName}`;
      

      // First, let's check if we can list buckets to verify connection
      const { data: buckets, error: bucketsError } = await supabase.storage.listBuckets();
      if (bucketsError) {
        console.error('Error listing buckets:', bucketsError);
      }

      const { error: uploadError } = await supabase.storage
        .from('logos')
        .upload(filePath, logoFile);

      if (uploadError) {
        console.error('Upload error details:', uploadError);
        throw uploadError;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('logos')
        .getPublicUrl(filePath);

      await updateCustomLogo(publicUrl);
      setLogoUrl(publicUrl);
      setPreviewLogoUrl(publicUrl);
      setLogoFile(null);
      
      toast({
        title: 'Logo updated',
        description: 'Custom logo has been uploaded successfully',
      });
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast({
        title: 'Error',
        description: 'Failed to upload logo. Please check your file and try again.',
        variant: 'destructive',
      });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleFaviconFileSelect = (file: File) => {
    if (!file) return;
    
    setFaviconFile(file);
    // Create a preview URL for the selected file
    const previewUrl = URL.createObjectURL(file);
    setPreviewFaviconUrl(previewUrl);
    
    toast({
      title: 'Favicon selected',
      description: 'Click "Update Favicon" to save the changes',
    });
  };

  const handleFaviconUpload = async () => {
    if (!faviconFile) {
      toast({
        title: 'No file selected',
        description: 'Please select a favicon file first',
        variant: 'destructive',
      });
      return;
    }

    setUploadingFavicon(true);
    try {
      // Get the current tenant ID dynamically
      const tenantId = await getCurrentTenantId();
      
      const fileExt = faviconFile.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `${tenantId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('logos')
        .upload(filePath, faviconFile);

      if (uploadError) {
        throw uploadError;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('logos')
        .getPublicUrl(filePath);

      await updateCustomFavicon(publicUrl);
      setFaviconUrl(publicUrl);
      setPreviewFaviconUrl(publicUrl);
      setFaviconFile(null);
      
      toast({
        title: 'Favicon updated',
        description: 'Custom favicon has been uploaded successfully',
      });
    } catch (error) {
      console.error('Error uploading favicon:', error);
      toast({
        title: 'Error',
        description: 'Failed to upload favicon. Please check your file and try again.',
        variant: 'destructive',
      });
    } finally {
      setUploadingFavicon(false);
    }
  };

  const handleLogoUrlChange = async () => {
    try {
      await updateCustomLogo(logoUrl);
      setPreviewLogoUrl(logoUrl);
      toast({
        title: 'Logo updated',
        description: 'Custom logo URL has been updated',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update logo URL',
        variant: 'destructive',
      });
    }
  };

  const handleFaviconUrlChange = async () => {
    try {
      await updateCustomFavicon(faviconUrl);
      setPreviewFaviconUrl(faviconUrl);
      toast({
        title: 'Favicon updated',
        description: 'Custom favicon URL has been updated',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update favicon URL',
        variant: 'destructive',
      });
    }
  };

  const handleSidebarColorChange = async (color: string) => {
    if (!canManageTheme) {
      toast({
        title: 'Access Denied',
        description: 'Only administrators can change theme settings',
        variant: 'destructive',
      });
      return;
    }

    try {
      await updateSidebarColor(color);
      toast({
        title: 'Sidebar color updated',
        description: 'Sidebar color has been updated successfully',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update sidebar color',
        variant: 'destructive',
      });
    }
  };

  const handleCreateTheme = async (theme: Omit<ThemeConfig, 'id' | 'createdAt'>) => {
    try {
      await createCustomTheme(theme);
      setIsEditorOpen(false);
      toast({
        title: 'Theme created',
        description: 'Custom theme has been created successfully',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to create theme',
        variant: 'destructive',
      });
    }
  };

  const handleUpdateTheme = async (theme: Omit<ThemeConfig, 'id' | 'createdAt'>) => {
    if (!editingTheme) return;
    
    try {
      await updateCustomTheme(editingTheme.id, theme);
      setEditingTheme(null);
      setIsEditorOpen(false);
      toast({
        title: 'Theme updated',
        description: 'Custom theme has been updated successfully',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update theme',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteTheme = async (themeId: string) => {
    try {
      await deleteCustomTheme(themeId);
      toast({
        title: 'Theme deleted',
        description: 'Custom theme has been deleted successfully',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete theme',
        variant: 'destructive',
      });
    }
  };

  const handleSEOUpdate = (field: keyof SEOConfig, value: string) => {
    if (!canManageTheme) {
      toast({
        title: 'Access Denied',
        description: 'Only administrators can modify SEO settings',
        variant: 'destructive',
      });
      return;
    }

    const updatedSeoSettings = { ...seoSettings, [field]: value };
    setSeoSettings(updatedSeoSettings);
    setSeoHasChanges(true);
  };

  const handleSaveSEO = async () => {
    if (!canManageTheme) {
      toast({
        title: 'Access Denied',
        description: 'Only administrators can save SEO settings',
        variant: 'destructive',
      });
      return;
    }

    setSavingSEO(true);
    try {
      // Update the SEO settings using the context function
      await updateCustomSEO(seoSettings);
      setSeoHasChanges(false);

      toast({
        title: 'SEO Settings Saved',
        description: 'Your SEO settings have been saved successfully',
      });
    } catch (error) {
      console.error('Error saving SEO settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save SEO settings',
        variant: 'destructive',
      });
    } finally {
      setSavingSEO(false);
    }
  };

  const openEditor = (theme?: ThemeConfig) => {
    setEditingTheme(theme || null);
    setIsEditorOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-foreground">Theme Settings</h2>
        <p className="text-muted-foreground">
          Customize the appearance of your application with themes, logos, and favicons.
        </p>
        {!canManageTheme && (
          <div className="mt-4 p-4 bg-primary/5 border border-primary/20 rounded-lg">
            <p className="text-primary text-sm">
              <strong>Note:</strong> Only administrators can modify theme settings. You can view the current theme configuration.
            </p>
          </div>
        )}
      </div>

      <Tabs defaultValue="themes" className="space-y-4">
        <TabsList className="grid w-full grid-cols-6 bg-muted/50 border border-border h-10 p-0.5">
          <TabsTrigger value="themes" className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm !h-8 !px-3 !py-0 !my-0 !mx-0.5 !rounded-sm justify-center">
            <Palette className="w-4 h-4" />
            Themes
          </TabsTrigger>
          <TabsTrigger value="custom" className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm !h-8 !px-3 !py-0 !my-0 !mx-0.5 !rounded-sm justify-center">
            <Plus className="w-4 h-4" />
            Custom
          </TabsTrigger>
          <TabsTrigger value="logo" className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm !h-8 !px-3 !py-0 !my-0 !mx-0.5 !rounded-sm justify-center">
            <Image className="w-4 h-4" />
            Logo
          </TabsTrigger>
          <TabsTrigger value="favicon" className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm !h-8 !px-3 !py-0 !my-0 !mx-0.5 !rounded-sm justify-center">
            <Image className="w-4 h-4" />
            Favicon
          </TabsTrigger>
          <TabsTrigger value="sidebar" className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm !h-8 !px-3 !py-0 !my-0 !mx-0.5 !rounded-sm justify-center">
            <LayoutDashboard className="w-4 h-4" />
            Sidebar
          </TabsTrigger>
          <TabsTrigger value="seo" className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm !h-8 !px-3 !py-0 !my-0 !mx-0.5 !rounded-sm justify-center">
            <Search className="w-4 h-4" />
            SEO
          </TabsTrigger>
        </TabsList>

        <TabsContent value="themes" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {PRESET_THEMES.map((theme) => (
              <Card 
                key={theme.id} 
                className={`transition-all hover:shadow-md ${
                  currentTheme.id === theme.id ? 'ring-2 ring-primary' : ''
                } ${canManageTheme ? 'cursor-pointer' : 'cursor-default'}`}
                onClick={() => canManageTheme && handleThemeChange(theme)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{theme.name}</CardTitle>
                    {currentTheme.id === theme.id && (
                      <Check className="w-5 h-5 text-primary" />
                    )}
                  </div>
                  <CardDescription>{theme.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <div 
                        className="w-8 h-8 rounded border"
                        style={{ backgroundColor: theme.colors.primary }}
                      />
                      <div 
                        className="w-8 h-8 rounded border"
                        style={{ backgroundColor: theme.colors.secondary }}
                      />
                      <div 
                        className="w-8 h-8 rounded border"
                        style={{ backgroundColor: theme.colors.accent }}
                      />
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Primary: {theme.colors.primary}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="custom" className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-foreground">Custom Themes</h3>
              <p className="text-muted-foreground">Create and manage your own custom themes</p>
            </div>
            {canManageTheme && (
              <Dialog open={isEditorOpen} onOpenChange={setIsEditorOpen}>
                <DialogTrigger asChild>
                  <Button onClick={() => openEditor()}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Theme
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Create Custom Theme</DialogTitle>
                    <DialogDescription>
                      Design your own custom theme with personalized colors and branding.
                    </DialogDescription>
                  </DialogHeader>
                  <EnhancedThemeEditor
                    theme={editingTheme || undefined}
                    onSave={editingTheme ? handleUpdateTheme : handleCreateTheme}
                    onCancel={() => {
                      setIsEditorOpen(false);
                      setEditingTheme(null);
                    }}
                    isEditing={!!editingTheme}
                  />
                </DialogContent>
              </Dialog>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {themeSettings.customThemes.map((theme) => (
              <Card 
                key={theme.id} 
                className={`relative transition-all hover:shadow-md ${
                  currentTheme.id === theme.id ? 'ring-2 ring-primary' : ''
                } ${canManageTheme ? 'cursor-pointer' : 'cursor-default'}`}
                onClick={() => canManageTheme && handleThemeChange(theme)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{theme.name}</CardTitle>
                    <div className="flex items-center gap-1">
                      {currentTheme.id === theme.id && (
                        <Check className="w-5 h-5 text-primary" />
                      )}
                      {canManageTheme && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditor(theme);
                            }}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteTheme(theme.id);
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  <CardDescription>{theme.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <div 
                        className="w-8 h-8 rounded border"
                        style={{ backgroundColor: theme.colors.primary }}
                      />
                      <div 
                        className="w-8 h-8 rounded border"
                        style={{ backgroundColor: theme.colors.secondary }}
                      />
                      <div 
                        className="w-8 h-8 rounded border"
                        style={{ backgroundColor: theme.colors.accent }}
                      />
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Primary: {theme.colors.primary}
                    </div>
                    {theme.createdAt && (
                      <div className="text-xs text-muted-foreground">
                        Created: {new Date(theme.createdAt).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {themeSettings.customThemes.length === 0 && (
            <Card>
              <CardContent className="text-center py-8">
                <Palette className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2 text-foreground">No Custom Themes</h3>
                <p className="text-muted-foreground mb-4">
                  Create your first custom theme to get started.
                </p>
                {canManageTheme && (
                  <Button onClick={() => openEditor()}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Theme
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="logo" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Custom Logo</CardTitle>
              <CardDescription>
                Upload a custom logo or provide a URL. The logo will be used throughout the application.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="logo-upload">Upload Logo</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="logo-upload"
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleLogoFileSelect(file);
                    }}
                    disabled={uploadingLogo || !canManageTheme}
                  />
                  {logoFile && (
                    <Button 
                      onClick={handleLogoUpload} 
                      disabled={uploadingLogo || !canManageTheme}
                      className="flex items-center gap-2"
                    >
                      {uploadingLogo ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        'Update Logo'
                      )}
                    </Button>
                  )}
                </div>
                {logoFile && (
                  <p className="text-sm text-muted-foreground">
                    Selected: {logoFile.name} ({(logoFile.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="logo-url">Logo URL</Label>
                <div className="flex gap-2">
                  <Input
                    id="logo-url"
                    type="url"
                    placeholder="https://example.com/logo.png"
                    value={logoUrl}
                    onChange={(e) => setLogoUrl(e.target.value)}
                    disabled={!canManageTheme}
                  />
                  <Button 
                    onClick={handleLogoUrlChange} 
                    disabled={!logoUrl || !canManageTheme}
                    className="flex items-center gap-2"
                  >
                    Update URL
                  </Button>
                </div>
              </div>

              {(previewLogoUrl || logoUrl) && (
                <div className="space-y-2">
                  <Label>Preview</Label>
                  <div className="border rounded-lg p-4 bg-muted/50">
                    <img 
                      src={previewLogoUrl || logoUrl} 
                      alt="Logo preview" 
                      className="max-h-20 max-w-full object-contain"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                  {previewLogoUrl !== logoUrl && (
                    <p className="text-sm text-muted-foreground">
                      This is a preview. Click "Update Logo" or "Update URL" to save changes.
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="favicon" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Custom Favicon</CardTitle>
              <CardDescription>
                Upload a custom favicon or provide a URL. The favicon appears in browser tabs.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="favicon-upload">Upload Favicon</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="favicon-upload"
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFaviconFileSelect(file);
                    }}
                    disabled={uploadingFavicon || !canManageTheme}
                  />
                  {faviconFile && (
                    <Button 
                      onClick={handleFaviconUpload} 
                      disabled={uploadingFavicon || !canManageTheme}
                      className="flex items-center gap-2"
                    >
                      {uploadingFavicon ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        'Update Favicon'
                      )}
                    </Button>
                  )}
                </div>
                {faviconFile && (
                  <p className="text-sm text-muted-foreground">
                    Selected: {faviconFile.name} ({(faviconFile.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="favicon-url">Favicon URL</Label>
                <div className="flex gap-2">
                  <Input
                    id="favicon-url"
                    type="url"
                    placeholder="https://example.com/favicon.ico"
                    value={faviconUrl}
                    onChange={(e) => setFaviconUrl(e.target.value)}
                    disabled={!canManageTheme}
                  />
                  <Button 
                    onClick={handleFaviconUrlChange} 
                    disabled={!faviconUrl || !canManageTheme}
                    className="flex items-center gap-2"
                  >
                    Update URL
                  </Button>
                </div>
              </div>

              {(previewFaviconUrl || faviconUrl) && (
                <div className="space-y-2">
                  <Label>Preview</Label>
                  <div className="border rounded-lg p-4 bg-muted/50">
                    <img 
                      src={previewFaviconUrl || faviconUrl} 
                      alt="Favicon preview" 
                      className="w-8 h-8 object-contain"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                  {previewFaviconUrl !== faviconUrl && (
                    <p className="text-sm text-muted-foreground">
                      This is a preview. Click "Update Favicon" or "Update URL" to save changes.
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sidebar" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Sidebar Color</CardTitle>
              <CardDescription>
                Choose the color for the application sidebar. You can keep it black or make it adapt to the theme.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="sidebar-black"
                    name="sidebar-color"
                    value="black"
                    checked={currentTheme.isCustom ? currentTheme.colors.sidebarColor === '#000000' : themeSettings.sidebarColorOverride === '#000000'}
                    onChange={() => handleSidebarColorChange('#000000')}
                    disabled={!canManageTheme}
                    className="text-primary focus:ring-primary"
                  />
                  <label htmlFor="sidebar-black" className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded border-2 border-border" style={{ backgroundColor: 'hsl(var(--foreground))' }}></div>
                    <span>Black Sidebar</span>
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="sidebar-theme"
                    name="sidebar-color"
                    value="theme"
                    checked={currentTheme.isCustom ? 
                      currentTheme.colors.sidebarColor === getAdaptiveSidebarColor(currentTheme.colors.primary, currentTheme.colors.background) : 
                      (!themeSettings.sidebarColorOverride || themeSettings.sidebarColorOverride === getAdaptiveSidebarColor(currentTheme.colors.primary, currentTheme.colors.background))}
                    onChange={() => handleSidebarColorChange(getAdaptiveSidebarColor(currentTheme.colors.primary, currentTheme.colors.background))}
                    disabled={!canManageTheme}
                    className="text-primary focus:ring-primary"
                  />
                  <label htmlFor="sidebar-theme" className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded border-2 border-border" style={{ backgroundColor: getAdaptiveSidebarColor(currentTheme.colors.primary, currentTheme.colors.background) }}></div>
                    <span>Theme-Adaptive Sidebar</span>
                  </label>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="use-custom-color"
                      checked={!!(currentTheme.isCustom ? 
                        (currentTheme.colors.sidebarColor !== '#000000' && currentTheme.colors.sidebarColor !== getAdaptiveSidebarColor(currentTheme.colors.primary, currentTheme.colors.background)) :
                        (themeSettings.sidebarColorOverride && 
                          themeSettings.sidebarColorOverride !== '#000000' && 
                          themeSettings.sidebarColorOverride !== getAdaptiveSidebarColor(currentTheme.colors.primary, currentTheme.colors.background)))}
                      onChange={(e) => {
                        if (e.target.checked) {
                          // Enable custom color - use current sidebar color or default to a nice color
                          const currentColor = currentTheme.colors.sidebarColor;
                          const adaptiveColor = getAdaptiveSidebarColor(currentTheme.colors.primary, currentTheme.colors.background);
                          const customColor = (currentColor === '#000000' || currentColor === adaptiveColor) ? '#3b82f6' : currentColor;
                          handleSidebarColorChange(customColor);
                        } else {
                          // Disable custom color - switch to theme-adaptive
                          handleSidebarColorChange(getAdaptiveSidebarColor(currentTheme.colors.primary, currentTheme.colors.background));
                        }
                      }}
                      disabled={!canManageTheme}
                      className="text-primary focus:ring-primary"
                    />
                    <label htmlFor="use-custom-color" className="text-sm font-medium">
                      Use Custom Color
                    </label>
                  </div>

                  <div className={`space-y-2 transition-opacity duration-200 ${
                    (currentTheme.isCustom ? 
                      (currentTheme.colors.sidebarColor !== '#000000' && currentTheme.colors.sidebarColor !== getAdaptiveSidebarColor(currentTheme.colors.primary, currentTheme.colors.background)) :
                      (themeSettings.sidebarColorOverride && 
                        themeSettings.sidebarColorOverride !== '#000000' && 
                        themeSettings.sidebarColorOverride !== getAdaptiveSidebarColor(currentTheme.colors.primary, currentTheme.colors.background))) 
                      ? 'opacity-100' : 'opacity-50'
                  }`}>
                    <Label htmlFor="custom-sidebar-color">Custom Sidebar Color</Label>
                    <div className="flex gap-2">
                      <Input
                        id="custom-sidebar-color"
                        type="color"
                        value={currentTheme.colors.sidebarColor}
                        onChange={(e) => handleSidebarColorChange(e.target.value)}
                        disabled={!canManageTheme || !(currentTheme.isCustom ? 
                          (currentTheme.colors.sidebarColor !== '#000000' && currentTheme.colors.sidebarColor !== getAdaptiveSidebarColor(currentTheme.colors.primary, currentTheme.colors.background)) :
                          (themeSettings.sidebarColorOverride && 
                            themeSettings.sidebarColorOverride !== '#000000' && 
                            themeSettings.sidebarColorOverride !== getAdaptiveSidebarColor(currentTheme.colors.primary, currentTheme.colors.background)))}
                        className="w-20 h-10"
                      />
                      <Input
                        type="text"
                        value={currentTheme.colors.sidebarColor}
                        onChange={(e) => handleSidebarColorChange(e.target.value)}
                        disabled={!canManageTheme || !(currentTheme.isCustom ? 
                          (currentTheme.colors.sidebarColor !== '#000000' && currentTheme.colors.sidebarColor !== getAdaptiveSidebarColor(currentTheme.colors.primary, currentTheme.colors.background)) :
                          (themeSettings.sidebarColorOverride && 
                            themeSettings.sidebarColorOverride !== '#000000' && 
                            themeSettings.sidebarColorOverride !== getAdaptiveSidebarColor(currentTheme.colors.primary, currentTheme.colors.background)))}
                        placeholder="#000000"
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                <h4 className="font-medium mb-2">Preview</h4>
                <div className="flex gap-4">
                  <div className="w-32 h-24 rounded border" style={{ backgroundColor: currentTheme.colors.sidebarColor }}>
                    <div className="p-2 text-white text-xs">
                      Sidebar Preview
                    </div>
                  </div>
                  <div className="flex-1 bg-background border rounded p-2">
                    <div className="text-sm text-muted-foreground">
                      Main content area
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="seo" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>SEO Metadata</CardTitle>
              <CardDescription>
                Configure search engine optimization settings for your application. These settings help improve your site's visibility in search results.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Basic SEO */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-foreground">Basic SEO</h3>
                
                <div className="space-y-2">
                  <Label htmlFor="page-title">Page Title</Label>
                  <Input
                    id="page-title"
                    placeholder="Enter page title (recommended: 50-60 characters)"
                    value={seoSettings.pageTitle || ''}
                    onChange={(e) => handleSEOUpdate('pageTitle', e.target.value)}
                    disabled={!canManageTheme}
                  />
                  <p className="text-sm text-muted-foreground">
                    {seoSettings.pageTitle?.length || 0}/60 characters
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="meta-description">Meta Description</Label>
                  <textarea
                    id="meta-description"
                    placeholder="Enter meta description (recommended: 150-160 characters)"
                    value={seoSettings.metaDescription || ''}
                    onChange={(e) => handleSEOUpdate('metaDescription', e.target.value)}
                    disabled={!canManageTheme}
                    className="w-full min-h-[80px] px-3 py-2 border border-input bg-background text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 rounded-md"
                  />
                  <p className="text-sm text-muted-foreground">
                    {seoSettings.metaDescription?.length || 0}/160 characters
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="meta-keywords">Meta Keywords</Label>
                  <Input
                    id="meta-keywords"
                    placeholder="Enter keywords separated by commas"
                    value={seoSettings.metaKeywords || ''}
                    onChange={(e) => handleSEOUpdate('metaKeywords', e.target.value)}
                    disabled={!canManageTheme}
                  />
                  <p className="text-sm text-muted-foreground">
                    Separate keywords with commas (e.g., solar, project management, installer)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="canonical-url">Canonical URL</Label>
                  <Input
                    id="canonical-url"
                    type="url"
                    placeholder="https://example.com"
                    value={seoSettings.canonicalUrl || ''}
                    onChange={(e) => handleSEOUpdate('canonicalUrl', e.target.value)}
                    disabled={!canManageTheme}
                  />
                  <p className="text-sm text-muted-foreground">
                    The preferred URL for this page (helps prevent duplicate content issues)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="robots-meta">Robots Meta Tag</Label>
                  <Select 
                    value={seoSettings.robotsMeta || 'index, follow'} 
                    onValueChange={(value) => handleSEOUpdate('robotsMeta', value)}
                    disabled={!canManageTheme}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="index, follow">Index, Follow</SelectItem>
                      <SelectItem value="noindex, follow">No Index, Follow</SelectItem>
                      <SelectItem value="index, nofollow">Index, No Follow</SelectItem>
                      <SelectItem value="noindex, nofollow">No Index, No Follow</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Open Graph */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-foreground">Open Graph (Social Media)</h3>
                
                <div className="space-y-2">
                  <Label htmlFor="og-title">OG Title</Label>
                  <Input
                    id="og-title"
                    placeholder="Enter Open Graph title"
                    value={seoSettings.ogTitle || ''}
                    onChange={(e) => handleSEOUpdate('ogTitle', e.target.value)}
                    disabled={!canManageTheme}
                  />
                  <p className="text-sm text-muted-foreground">
                    Title displayed when shared on social media (Facebook, LinkedIn, etc.)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="og-description">OG Description</Label>
                  <textarea
                    id="og-description"
                    placeholder="Enter Open Graph description"
                    value={seoSettings.ogDescription || ''}
                    onChange={(e) => handleSEOUpdate('ogDescription', e.target.value)}
                    disabled={!canManageTheme}
                    className="w-full min-h-[80px] px-3 py-2 border border-input bg-background text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 rounded-md"
                  />
                  <p className="text-sm text-muted-foreground">
                    Description displayed when shared on social media
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="og-image">OG Image URL</Label>
                  <Input
                    id="og-image"
                    type="url"
                    placeholder="https://example.com/image.jpg"
                    value={seoSettings.ogImage || ''}
                    onChange={(e) => handleSEOUpdate('ogImage', e.target.value)}
                    disabled={!canManageTheme}
                  />
                  <p className="text-sm text-muted-foreground">
                    Image displayed when shared on social media (recommended: 1200x630px)
                  </p>
                </div>
              </div>

              {/* Twitter Card */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-foreground">Twitter Card</h3>
                
                <div className="space-y-2">
                  <Label htmlFor="twitter-card">Twitter Card Type</Label>
                  <Select 
                    value={seoSettings.twitterCard || 'summary_large_image'} 
                    onValueChange={(value) => handleSEOUpdate('twitterCard', value)}
                    disabled={!canManageTheme}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="summary">Summary</SelectItem>
                      <SelectItem value="summary_large_image">Summary Large Image</SelectItem>
                      <SelectItem value="app">App</SelectItem>
                      <SelectItem value="player">Player</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="twitter-title">Twitter Title</Label>
                  <Input
                    id="twitter-title"
                    placeholder="Enter Twitter title"
                    value={seoSettings.twitterTitle || ''}
                    onChange={(e) => handleSEOUpdate('twitterTitle', e.target.value)}
                    disabled={!canManageTheme}
                  />
                  <p className="text-sm text-muted-foreground">
                    Title displayed when shared on Twitter
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="twitter-description">Twitter Description</Label>
                  <textarea
                    id="twitter-description"
                    placeholder="Enter Twitter description"
                    value={seoSettings.twitterDescription || ''}
                    onChange={(e) => handleSEOUpdate('twitterDescription', e.target.value)}
                    disabled={!canManageTheme}
                    className="w-full min-h-[80px] px-3 py-2 border border-input bg-background text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 rounded-md"
                  />
                  <p className="text-sm text-muted-foreground">
                    Description displayed when shared on Twitter
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="twitter-image">Twitter Image URL</Label>
                  <Input
                    id="twitter-image"
                    type="url"
                    placeholder="https://example.com/twitter-image.jpg"
                    value={seoSettings.twitterImage || ''}
                    onChange={(e) => handleSEOUpdate('twitterImage', e.target.value)}
                    disabled={!canManageTheme}
                  />
                  <p className="text-sm text-muted-foreground">
                    Image displayed when shared on Twitter (recommended: 1200x600px)
                  </p>
                </div>
              </div>

              {/* Preview */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-foreground">Preview</h3>
                <div className="p-4 bg-muted/50 rounded-lg space-y-4">
                  <div>
                    <h4 className="font-medium text-sm text-muted-foreground mb-2">Search Result Preview</h4>
                    <div className="space-y-1">
                      <div className="text-primary text-sm font-medium truncate">
                        {seoSettings.pageTitle || 'Page Title'}
                      </div>
                      <div className="text-green-600 dark:text-green-400 text-sm">
                        {seoSettings.canonicalUrl || 'https://example.com'}
                      </div>
                      <div className="text-muted-foreground text-sm">
                        {seoSettings.metaDescription || 'Meta description will appear here...'}
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-sm text-muted-foreground mb-2">Social Media Preview</h4>
                    <div className="space-y-1">
                      <div className="text-sm font-medium">
                        {seoSettings.ogTitle || seoSettings.pageTitle || 'Title'}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {seoSettings.ogDescription || seoSettings.metaDescription || 'Description will appear here...'}
                      </div>
                      {seoSettings.ogImage && (
                        <div className="text-xs text-muted-foreground">
                          Image: {seoSettings.ogImage}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end pt-6 border-t">
                <div className="flex items-center gap-4">
                  {seoHasChanges && (
                    <span className="text-sm text-muted-foreground">
                      You have unsaved changes
                    </span>
                  )}
                  <Button
                    onClick={handleSaveSEO}
                    disabled={!canManageTheme || !seoHasChanges || savingSEO}
                    className="min-w-[100px]"
                  >
                    {savingSEO ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Save SEO
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}; 